
import { defineComponent, computed, ref, reactive, watch, onMounted, watchEffect, getCurrentInstance } from 'vue';
import { message } from 'ant-design-vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import moment, { Moment } from 'moment';
import { UnwrapNestedRefs } from '@vue/reactivity';
import { settleTree, formatWithIntl, noEmptyProp, not } from '@/utils/illuminate';
import { createApiUrl } from '@/utils/utils';
import type { TableColumn } from '@/components/Table';
import { request } from '@/utils/request';
import { getHoupiaoStore, HOUPIAO_FORM_KEY } from '@/views/fapiao/parts/utils';
import { EnterpriseInputItem } from '@/views/parts/subsidiary';
import routeTab from './parts/routeTab.vue';

type EnergyMode = 'oil' | 'charge';

const OIL_COLUMNS: TableColumn[] = [
  { title: '订单编号', dataIndex: 'orderNo', width: '180px', align: 'center' },
  { title: '部门', dataIndex: 'accountMainDeptName', width: '150px', align: 'center', customRender: ({ text }) => text || '--' },
  { title: '员工姓名', dataIndex: 'userName', width: '120px', align: 'center' },
  { title: '手机号码', dataIndex: 'phone', width: '140px', align: 'center' },
  { title: '车辆', slots: { customRender: 'licensePlate' }, width: '150px', align: 'center' },
  { title: '订单服务费总金额', dataIndex: 'serviceFeeTotalInvoiceAmount', width: '160px', align: 'center', customRender: ({ text }) => (text === null || text === undefined || text === '' ? '--' : (+text).toFixed(2)) },
  { title: '订单总金额', dataIndex: 'totalOrderAmount', width: '140px', align: 'center', customRender: ({ text }) => (text === null || text === undefined || text === '' ? '--' : formatWithIntl(text)) },
  { title: '油号', dataIndex: 'oilName', width: '100px', align: 'center' },
  { title: '油站', dataIndex: 'gasName', width: '160px', align: 'center' },
  { title: '能源类型', dataIndex: 'energyTypeStr', width: '110px', align: 'center' },
  { title: '支付方式', dataIndex: 'payTypeStr', width: '120px', align: 'center' },
  { title: '支付时间', dataIndex: 'payTime', width: '170px', align: 'center' },
  { title: '下单时间', dataIndex: 'createOrderDate', width: '170px', align: 'center' },
];

const CHARGE_COLUMNS: TableColumn[] = [
  { title: '订单编号', dataIndex: 'orderNo', width: '200px', align: 'center' },
  { title: '部门', dataIndex: 'accountMainDeptName', width: '150px', align: 'center', customRender: ({ text }) => text || '--' },
  { title: '员工姓名', dataIndex: 'userName', width: '120px', align: 'center' },
  { title: '手机号码', dataIndex: 'phone', width: '140px', align: 'center' },
  { title: '车辆', slots: { customRender: 'licensePlate' }, width: '150px', align: 'center' },
  { title: '订单服务费总金额', dataIndex: 'serviceFeeTotalInvoiceAmount', width: '160px', align: 'center', customRender: ({ text }) => (text === null || text === undefined || text === '' ? '--' : (+text).toFixed(2)) },
  { title: '订单总金额', dataIndex: 'totalOrderAmount', width: '140px', align: 'center', customRender: ({ text }) => (text === null || text === undefined || text === '' ? '--' : formatWithIntl(text)) },
  { title: '最终实付', dataIndex: 'totalRealAmount', width: '140px', align: 'center', customRender: ({ text }) => (text === null || text === undefined || text === '' ? '--' : (+text).toFixed(2)) },
  { title: '站点名称', dataIndex: 'stationName', width: '180px', align: 'center' },
  { title: '度数', dataIndex: 'chargingDegree', width: '110px', align: 'center' },
  { title: '支付方式', dataIndex: 'payTypeDesc', width: '120px', align: 'center' },
  { title: '支付时间', dataIndex: 'payTime', width: '170px', align: 'center' },
  { title: '下单时间', dataIndex: 'createDate', width: '170px', align: 'center' },
];

const makeDefaultFilterParams = (enterprise: EnterpriseInputItem) => ({
  usercode: undefined as string | undefined,
  vehicleId: undefined as string | undefined,
  energyType: '' as number | string,
  dateRange: [] as Moment[],
  payTimeRange: [] as Moment[],
  enterpriseId: enterprise.enterpriseId as number | string,
  departmentId: undefined as number | undefined,
  enterprise,
  orderNo: undefined as string | undefined,
});

export default defineComponent({
  name: 'ServiceFeeInvoiceView',
  components: { routeTab },
  setup() {
    const instance = getCurrentInstance()?.proxy;
    const router = useRouter();
    const store = useStore();

    const energyMode = ref<EnergyMode>('oil');

    const filterParams = reactive(makeDefaultFilterParams(store.state.User.enterprise));

    const tableColumns = ref<TableColumn[]>(OIL_COLUMNS);
    const tableRef = ref<any>(null);

    const checkedAll = ref(false);
    const isOverActionTip = ref(false);

    const statistics = ref({ orderNum: 0, totalAmount: 0 });

    const actionTipVisible = computed({
      get(): boolean { return !statistics.value.orderNum && isOverActionTip.value; },
      set(val: boolean) { isOverActionTip.value = val; },
    });

    let selectState: UnwrapNestedRefs<{
      selectedRowKeys: any[];
      selectedRows: any[];
      hideDefaultSelections: boolean;
      onChange: (selectedRowKeys: any[], selectedRows: any[]) => void,
    }>;

    /* --------------- 员工 / 车辆 搜索 --------------- */
    const userList = ref<Array<Record<string, any>>>([]);
    const handleUserSearch = async (val: string) => {
      if (!val) return;
      const params = { nameOrPhone: val, enterpriseId: filterParams.enterpriseId, status: 'ALL' };
      const res: any = await request.get(createApiUrl('/newlinkSass/enterprise/find-user-list-by-name-or-phone'), { params });
      if (res.code !== 200) return message.error(res.message);
      userList.value = res.data;
    };

    const vehicleList = ref<Array<Record<string, string>>>([]);
    const handleVehicleSearch = async (value: any) => {
      if (!value) return;
      const params = { licensePlateOrVin: value, enterpriseId: filterParams.enterpriseId };
      const res: any = await request.get(createApiUrl('/newlinkSass/vehicle/fuzzySearchVehicle'), { params });
      if (res.code !== 200) return message.error(res.message);
      vehicleList.value = res.data;
    };

    /* --------------- 能源类型选单（加油） --------------- */
    const energyTypeList = ref<{ code: string; name: string }[]>([]);
    const loadEnergyType = async () => {
      try {
        const res: any = await request.get(createApiUrl('/newlinkSass/invoice/get-order-energy-type-list'));
        energyTypeList.value = [{ code: '', name: '全部' }].concat(res.data || []);
        filterParams.energyType = '';
      } catch (e: Error & any) {
        message.error(e.message);
      }
    };

    /* --------------- 请求参数拼装 --------------- */
    const buildParams = () => {
      const params: any = {
        ...noEmptyProp(not(filterParams, ['dateRange', 'payTimeRange', 'enterprise', 'energyType'])),
        enterpriseId: filterParams.enterprise?.enterpriseId,
        departmentId: filterParams.departmentId,
      };
      if (filterParams.dateRange.length) {
        params.startTime = filterParams.dateRange[0].valueOf();
        params.endTime = filterParams.dateRange[1].valueOf();
      }
      if (filterParams.payTimeRange.length) {
        params.payStartTime = filterParams.payTimeRange[0].valueOf();
        params.payEndTime = filterParams.payTimeRange[1].valueOf();
      }
      if (energyMode.value === 'oil' && filterParams.energyType !== '' && filterParams.energyType !== undefined) {
        params.energyType = filterParams.energyType;
      }
      return params;
    };

    /* --------------- 统计 --------------- */
    const invoiceOrderStatistics = async () => {
      const params = buildParams();
      params.orderNos = selectState.selectedRowKeys;

      const url = energyMode.value === 'oil'
        ? '/newlinkSass/invoice/findServiceFeeInvoiceStatistics'
        : '/newlinkSass/invoice/findEleServiceFeeInvoiceStatistics';

      try {
        const res: any = await request.post(createApiUrl(url), params);
        const data = res.data || {};
        statistics.value = energyMode.value === 'oil'
          ? { orderNum: data.orderNum || 0, totalAmount: data.totalAmount || 0 }
          : { orderNum: data.eleOrderServicerNum || 0, totalAmount: data.eleOrderServiceAmount || 0 };
      } catch (e: Error & any) {
        message.error(e.message);
      }
    };

    /* --------------- 列表 --------------- */
    const loadTableData = async (parameter: any) => {
      if (!filterParams.dateRange.length && !filterParams.payTimeRange.length) return { list: [] };
      const params = buildParams();

      const url = energyMode.value === 'oil'
        ? '/newlinkSass/invoice/findEnterpriseServiceFeeInvoiceOrderRes'
        : '/newlinkSass/invoice/find-center-invoice-ele-service-fee-order-list';

      try {
        const res: any = await request.post(createApiUrl(url), { ...parameter, ...params });
        if (energyMode.value === 'oil') {
          return { list: res.data || [] };
        }
        return res.data || { list: [] };
      } catch (e: Error & any) {
        message.error(e.message);
        return { list: [] };
      }
    };

    const onRefreshTable = () => tableRef.value?.refresh(true);
    const onClearTable = () => tableRef.value?.clear(true);

    /* --------------- 复选交互 --------------- */
    const handleCheckedAll = (e: any) => {
      if (e.target.checked) {
        selectState.selectedRowKeys = [];
        invoiceOrderStatistics();
      } else statistics.value = { orderNum: 0, totalAmount: 0 };
    };

    const onSelectChange = (selectedRowKeys: any[], selectedRows: any[]) => {
      selectState.selectedRows = selectedRows;
      selectState.selectedRowKeys = selectedRowKeys;
      if (!selectedRowKeys.length) {
        statistics.value = { orderNum: 0, totalAmount: 0 };
        return;
      }
      if ((filterParams.dateRange.length || filterParams.payTimeRange.length) && !checkedAll.value) invoiceOrderStatistics();
    };

    selectState = reactive({
      selectedRowKeys: [],
      selectedRows: [],
      hideDefaultSelections: true,
      onChange: onSelectChange,
    });

    watch(filterParams, val => {
      if (val.dateRange.length || val.payTimeRange.length) {
        onRefreshTable();
        invoiceOrderStatistics();
        checkedAll.value = true;
      } else {
        onClearTable();
        statistics.value = { orderNum: 0, totalAmount: 0 };
      }
    });

    /* --------------- 分段切换（清空当前批次） --------------- */
    const resetFilterParams = () => {
      const defaults = makeDefaultFilterParams(store.state.User.enterprise);
      Object.assign(filterParams, defaults);
    };

    const onEnergyModeChange = () => {
      tableColumns.value = energyMode.value === 'oil' ? OIL_COLUMNS : CHARGE_COLUMNS;
      resetFilterParams();
      selectState.selectedRows = [];
      selectState.selectedRowKeys = [];
      checkedAll.value = false;
      statistics.value = { orderNum: 0, totalAmount: 0 };
      userList.value = [];
      vehicleList.value = [];
      onClearTable();
    };

    /* --------------- 跳转开票 --------------- */
    const onGotoForm = () => {
      if (!statistics.value.orderNum) return message.error('请筛选开票订单!');
      const storage = getHoupiaoStore();
      const params: any = { ...not(filterParams, ['dateRange']) };
      if (filterParams.dateRange.length) {
        params.startTime = filterParams.dateRange[0].valueOf();
        params.endTime = filterParams.dateRange[1].valueOf();
      }
      params.orderNos = selectState.selectedRowKeys;
      params.energyMode = energyMode.value;
      storage.set(HOUPIAO_FORM_KEY, params);
      router.push({ path: '/fapiao/issue-houpiao/form', query: { type: 'serviceFee', energyMode: energyMode.value } });
    };

    /* --------------- 部门树 --------------- */
    const departmentList = ref<any[]>([]);
    const loadEnterpriseDepartment = async (val: any) => {
      filterParams.departmentId = undefined;
      filterParams.usercode = undefined;
      filterParams.vehicleId = undefined;
      vehicleList.value = [];
      userList.value = [];
      if (val === '') {
        departmentList.value = [];
        return;
      }
      try {
        const res: any = await request.get(createApiUrl('/newlinkSass/enterprise/find-enterprise-department'), { params: { enterpriseId: val } });
        departmentList.value = settleTree(res.data.departmentList, { key: 'id', title: 'departmentName', value: 'id' }, 'departmentList');
      } catch (e: Error & any) {
        message.error(e.message);
      }
    };

    const onChangeDate = (val: Moment[]) => {
      if (!val || !val.length) {
        filterParams.dateRange = [];
        return;
      }
      filterParams.dateRange = [moment(val[0].format('YYYY-MM-DD HH:mm:ss')), moment(val[1].format('YYYY-MM-DD HH:mm:ss'))];
    };

    watchEffect(() => {
      loadEnterpriseDepartment(filterParams.enterpriseId);
    });

    onMounted(() => {
      instance?.$sensors?.track?.('es_saas_tax_gas_page_view', { page_name: '发票管理_服务费开发票_浏览' });
      loadEnergyType();
    });

    return {
      energyMode,
      onEnergyModeChange,

      statistics,
      filterParams,
      departmentList,
      isOverActionTip,
      actionTipVisible,
      tableColumns,
      onChangeDate,

      tableRef,
      loadTableData,
      onRefreshTable,
      onGotoForm,

      energyTypeList,
      vehicleList,
      handleVehicleSearch,
      userList,
      handleUserSearch,
      selectState,
      checkedAll,
      handleCheckedAll,
      moment,
    };
  },
});
